import { EProjectType } from '../enum/project';
import { ConversionUtils } from '../Dynamic/components/InputForm/FieldUtils';
import { logDebug } from '../utils/utils';
import { ProjectData } from '../api/IApiResponse';
import { normalizeNumberFieldValue } from './WireValueNormalizer';

/**
 * Service for transforming project data before API submission
 */
export class ProjectDataTransformService {
    /**
     * Transform subgrade data to ensure backend always receives cbrSubgrade value
     * Converts deformationModulus (ev2) to cbrSubgrade when subgradeInputMethod is 'modulus'
     *
     * @param data - The project data to transform
     * @param projectMethod - The calculation method (e.g., 'static', 'bcr')
     * @param projectType - The project type (e.g., 'container_yard', 'parking_deck')
     * @returns Transformed data with cbrSubgrade values
     */
    static transformSubgradeData(data: ProjectData, projectMethod: string, projectType: EProjectType): ProjectData {
        if (!data || !projectMethod) return data;

        // Handle both container_yard and parking_deck projects
        if (projectType === EProjectType.container_yard || projectType === EProjectType.parking_deck) {
            let subgrade = null;
            let methodDataPath = null;
            let subgradeFieldName: string | null = null;

            // Check if data is NESTED (has [projectMethod] key) or FLAT
            const hasMethodKey = (data as any)[projectMethod] && typeof (data as any)[projectMethod] === 'object';

            if (hasMethodKey) {
                // NESTED structure: data is under [projectMethod]
                const methodData = (data as any)[projectMethod] as Record<string, unknown>;

                // Check for multiple possible subgrade field paths and names
                if (methodData.bcr && typeof methodData.bcr === 'object' && methodData.bcr !== null) {
                    const bcrData = methodData.bcr as Record<string, unknown>;
                    if (bcrData.subgradeCharatteristic) {
                        subgrade = bcrData.subgradeCharatteristic;
                        methodDataPath = 'bcr';
                        subgradeFieldName = 'subgradeCharatteristic';
                    } else if (bcrData.subgrade) {
                        subgrade = bcrData.subgrade;
                        methodDataPath = 'bcr';
                        subgradeFieldName = 'subgrade';
                    }
                }

                if (!subgrade && methodData.static && typeof methodData.static === 'object' && methodData.static !== null) {
                    const staticData = methodData.static as Record<string, unknown>;
                    if (staticData.subgradeCharatteristic) {
                        subgrade = staticData.subgradeCharatteristic;
                        methodDataPath = 'static';
                        subgradeFieldName = 'subgradeCharatteristic';
                    } else if (staticData.subgrade) {
                        subgrade = staticData.subgrade;
                        methodDataPath = 'static';
                        subgradeFieldName = 'subgrade';
                    }
                }

                if (!subgrade) {
                    // Direct path for some methods
                    if (methodData.subgradeCharatteristic) {
                        subgrade = methodData.subgradeCharatteristic;
                        methodDataPath = 'direct';
                        subgradeFieldName = 'subgradeCharatteristic';
                    } else if (methodData.subgrade) {
                        subgrade = methodData.subgrade;
                        methodDataPath = 'direct';
                        subgradeFieldName = 'subgrade';
                    }
                }
            } else {
                // FLAT structure: data is directly on root
                logDebug('ProjectDataTransformService', 'Detected FLAT structure, checking for direct subgrade access');

                // Check for subgrade directly on data (FLAT structure)
                if ((data as any).subgradeCharatteristic) {
                    subgrade = (data as any).subgradeCharatteristic;
                    methodDataPath = 'flat';
                    subgradeFieldName = 'subgradeCharatteristic';
                } else if ((data as any).subgrade) {
                    subgrade = (data as any).subgrade;
                    methodDataPath = 'flat';
                    subgradeFieldName = 'subgrade';
                }
            }

            logDebug('ProjectDataTransformService', `Subgrade detection - method: ${projectMethod}, path: ${methodDataPath}, field: ${subgradeFieldName}`, subgrade);

            if (subgrade && typeof subgrade === 'object' && subgrade !== null && methodDataPath && subgradeFieldName) {
                const subgradeData = subgrade as Record<string, unknown>;
                if (subgradeData.subgradeInputMethod === 'modulus' && subgradeData.deformationModulus) {
                    return this.transformModulusToCBR(data as unknown as Record<string, unknown>, projectMethod, subgradeData, methodDataPath, subgradeFieldName) as unknown as ProjectData;
                } else if (subgradeData.subgradeInputMethod === 'cbr' && subgradeData.cbrSubgrade) {
                    return this.normalizeCBRData(data as unknown as Record<string, unknown>, projectMethod, subgradeData, methodDataPath, subgradeFieldName) as unknown as ProjectData;
                }
            }
        }
        return data;
    }

    /**
     * Convert deformationModulus (ev2) to cbrSubgrade
     */
    private static transformModulusToCBR(
        data: Record<string, unknown>,
        projectMethod: string,
        subgrade: Record<string, unknown>,
        methodDataPath: string,
        subgradeFieldName: string
    ): Record<string, unknown> {
        // Convert deformationModulus (ev2) to cbrSubgrade using the centralized conversion function
        const ev2 = normalizeNumberFieldValue(subgrade.deformationModulus);
        // A malformed modulus derives nothing: no CBR is invented from its numeric prefix.
        if (typeof ev2 !== 'number') return data;
        const cbrValue = ConversionUtils.ev2ToCbr(ev2);

        logDebug('ProjectDataTransformService', `Converting ev2 (${ev2}) to CBR (${cbrValue.toFixed(2)}) for method: ${projectMethod}, path: ${methodDataPath}, field: ${subgradeFieldName}`);

        const transformedSubgrade = {
            ...subgrade,
            cbrSubgrade: parseFloat(cbrValue.toFixed(2)), // Keep 2 decimal places
            // Keep original values for reference but backend will use cbrSubgrade
            subgradeInputMethod: subgrade.subgradeInputMethod,
            deformationModulus: subgrade.deformationModulus,
        };

        return this.updateDataWithTransformedSubgrade(data, projectMethod, transformedSubgrade, methodDataPath, subgradeFieldName);
    }

    /**
     * Normalize CBR data to ensure proper structure
     */
    private static normalizeCBRData(
        data: Record<string, unknown>,
        projectMethod: string,
        subgrade: Record<string, unknown>,
        methodDataPath: string,
        subgradeFieldName: string
    ): Record<string, unknown> {
        // CBR is already the correct format, just ensure it's properly structured
        logDebug('ProjectDataTransformService', `Using CBR value directly: ${subgrade.cbrSubgrade} for method: ${projectMethod}, path: ${methodDataPath}, field: ${subgradeFieldName}`);

        const transformedSubgrade = {
            ...subgrade,
            cbrSubgrade: normalizeNumberFieldValue(subgrade.cbrSubgrade),
            subgradeInputMethod: subgrade.subgradeInputMethod,
        };

        return this.updateDataWithTransformedSubgrade(data, projectMethod, transformedSubgrade, methodDataPath, subgradeFieldName);
    }

    /**
     * Update the data structure with the transformed subgrade
     */
    private static updateDataWithTransformedSubgrade(
        data: Record<string, unknown>,
        projectMethod: string,
        transformedSubgrade: Record<string, unknown>,
        methodDataPath: string,
        subgradeFieldName: string
    ): Record<string, unknown> {
        const methodData = data[projectMethod] as Record<string, unknown>;

        if (methodDataPath === 'bcr' && subgradeFieldName) {
            const bcrData = methodData.bcr as Record<string, unknown>;
            return {
                ...data,
                [projectMethod]: {
                    ...methodData,
                    bcr: {
                        ...bcrData,
                        [subgradeFieldName]: transformedSubgrade,
                    },
                },
            };
        } else if (methodDataPath === 'static' && subgradeFieldName) {
            const staticData = methodData.static as Record<string, unknown>;
            return {
                ...data,
                [projectMethod]: {
                    ...methodData,
                    static: {
                        ...staticData,
                        [subgradeFieldName]: transformedSubgrade,
                    },
                },
            };
        } else if (methodDataPath === 'direct' && subgradeFieldName) {
            return {
                ...data,
                [projectMethod]: {
                    ...methodData,
                    [subgradeFieldName]: transformedSubgrade,
                },
            };
        } else if (methodDataPath === 'flat' && subgradeFieldName) {
            // FLAT structure: update subgrade directly on data root
            return {
                ...data,
                [subgradeFieldName]: transformedSubgrade,
            };
        }

        return data;
    }
}
